"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ArgSelectCompact = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const use_t_1 = require("use-t");
const ContextItem_1 = require("../../ContextItem");
const ContextMenu_1 = require("../../ContextMenu");
const PopupControlled_1 = require("../../../Popup/PopupControlled");
const context_1 = require("../../../Popup/context");
const popup_1 = require("../../../../utils/popup");
const useLockScrolling_1 = require("../../../../hooks/useLockScrolling");
const useSingletonPopup_1 = require("../../../../hooks/useSingletonPopup");
const Iconista_1 = require("../../../../icons/Iconista");
const context_2 = require("../../../../styles/context");
const OptionalBadge_1 = require("./OptionalBadge");
const DefaultableToggle_1 = require("./DefaultableToggle");
const AutoValue_1 = require("./AutoValue");
const readStructured = (v) => {
    if (v && typeof v === 'object' && 'def' in v) {
        const s = v;
        return { def: !!s.def, value: String(s.value ?? '') };
    }
    return { def: false, value: typeof v === 'string' ? v : '' };
};
const ArgSelectCompact = ({ param, value, onChange }) => {
    const [t] = (0, use_t_1.useT)();
    const styles = (0, context_2.useStyles)();
    const label = param.display?.() ?? t(param.name ?? param.id ?? '');
    const options = param.options ?? [];
    const defaultable = !!param.defaultable;
    const s = readStructured(value);
    const def = defaultable && s.def;
    const emit = (next) => {
        if (defaultable)
            onChange?.(next);
        else
            onChange?.(next.value);
    };
    const setValue = (id) => emit({ def: false, value: id });
    const enterCustom = () => emit({ def: false, value: s.value });
    const revertToAuto = () => emit({ def: true, value: s.value });
    const defaultId = param.default ?? '';
    const displayId = def ? defaultId : s.value;
    const selected = options.find((o) => (o.id ?? o.name) === displayId);
    const selectedLabel = selected ? (selected.display?.() ?? selected.name ?? selected.id ?? '') : '—';
    // biome-ignore lint/correctness/useExhaustiveDependencies: setValue identity not material
    const menu = React.useMemo(() => ({
        name: param.name ?? param.id ?? '',
        noHeader: true,
        minWidth: 220,
        children: options.map((opt) => {
            const id = opt.id ?? opt.name;
            const isSelected = id === s.value;
            return {
                ...opt,
                onSelect: () => setValue(id ?? ''),
                right: isSelected ? () => React.createElement(Iconista_1.Iconista, { width: 14, height: 14, set: "radix", icon: "check" }) : opt.right,
            };
        }),
    }), [options, param.name, param.id, s.value]);
    const ellipsisStyle = {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: 160,
    };
    const selectedIcon = selected?.icon ? selected.icon() : null;
    const labelWithIcon = (color) => (React.createElement("span", { style: { display: 'inline-flex', alignItems: 'center', gap: 6, color } },
        React.createElement("span", { style: ellipsisStyle }, selectedLabel),
        selectedIcon && React.createElement("span", { style: { display: 'inline-flex' } }, selectedIcon)));
    const popup = (0, useSingletonPopup_1.useSingletonPopup)('arg-select');
    const closePopup = React.useCallback(() => popup.setOpen(false), [popup]);
    const popupContextValue = React.useMemo(() => ({ close: closePopup }), [closePopup]);
    const anchorHandle = (0, popup_1.useAnchorPointHandle)({ pinX: 'right' });
    (0, useLockScrolling_1.useLockScrolling)(popup.open);
    const dropRef = React.useRef(null);
    React.useEffect(() => {
        if (!popup.open)
            return;
        const handler = (e) => {
            const target = e.target;
            if (!target)
                return;
            const tog = anchorHandle.toggle;
            const drop = dropRef.current;
            if (tog && tog.contains(target))
                return;
            if (drop && drop.contains(target))
                return;
            closePopup();
        };
        // Listen in capture so we run before any internal mousedown handlers call stopPropagation.
        document.addEventListener('mousedown', handler, true);
        return () => document.removeEventListener('mousedown', handler, true);
    }, [popup.open, anchorHandle, closePopup]);
    if (defaultable && def) {
        return (React.createElement(ContextItem_1.ContextItem, { icon: param.icon?.(), control: true, inset: true, onClick: enterCustom, style: { paddingTop: 9, paddingBottom: 9 }, right: React.createElement("span", { style: { display: 'inline-flex', alignItems: 'center', gap: 6, marginRight: -8 } },
                React.createElement(DefaultableToggle_1.DefaultableToggle, { def: true, onClick: enterCustom }),
                React.createElement(AutoValue_1.AutoValue, { onClick: enterCustom }, labelWithIcon('inherit'))) },
            React.createElement("span", null,
                label,
                param.optional && React.createElement(OptionalBadge_1.OptionalBadge, null))));
    }
    const customLabel = labelWithIcon(styles.g(0.15));
    const showSearch = param.showSearch ?? options.length >= 6;
    return (React.createElement(context_1.context.Provider, { value: popupContextValue },
        React.createElement(popup_1.anchorContext.Provider, { value: anchorHandle },
            React.createElement(PopupControlled_1.PopupControlled, { block: true, open: popup.open, refToggle: anchorHandle.ref, onHeadClick: () => popup.setOpen(!popup.open), onClickAway: closePopup, onEsc: popup.open ? closePopup : undefined, renderContext: () => (React.createElement("div", { ref: dropRef },
                    React.createElement(ContextMenu_1.ContextMenu, { inset: true, showSearch: showSearch, searchPlaceholder: param.searchPlaceholder, menu: menu }))) },
                React.createElement(ContextItem_1.ContextItem, { icon: param.icon?.(), control: true, inset: true, nested: true, onClick: () => { }, style: { paddingTop: 9, paddingBottom: 9 }, right: defaultable ? (React.createElement("span", { style: { display: 'inline-flex', alignItems: 'center', gap: 6 } },
                        React.createElement(DefaultableToggle_1.DefaultableToggle, { def: false, onClick: revertToAuto }),
                        customLabel)) : (customLabel) },
                    React.createElement("span", null,
                        label,
                        param.optional && React.createElement(OptionalBadge_1.OptionalBadge, null)))))));
};
exports.ArgSelectCompact = ArgSelectCompact;
